import {
  type ColumnFragment,
  CreateNestedControlCardInstanceDocument,
  type ValueInstanceFragment
} from "app/gql/graphqlSchema";
import {
  IonBadge,
  IonButton,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  useIonLoading,
  useIonRouter
} from "@ionic/react";
import {isNotBlank} from "app/utils/stdlib";
import {FlexColumnCenter} from "app/employee/flexUtils";
import {useControlCardInstanceContext} from "app/employee/controlCard/ControlCardInstanceContext";
import {useHasControlCardValueError} from "app/employee/controlCard/hook/useHasControlCardValueError";
import {InputIonCard} from "app/employee/controlCard/input/InputIonCard";
import {DisplayValueInstanceError} from "app/employee/controlCard/input/DisplayValueInstanceError";
import {DisplayAppError} from "app/employee/controlCard/input/DisplayRootError";
import {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {addOutline, arrowForwardOutline} from "ionicons/icons";
import {query} from "app/gql/client";
import {linkEmplControlCardInstance} from "app/employee/core/employeeLinks";
import {type ApplicationError, transformToApplicationError} from "app/gql/handleGraphqlErrors";

interface Props {
  column: ColumnFragment & {__typename: "ControlCardNestedControlCardColumn"};
  valueInstance: ValueInstanceFragment;
  disabled?: boolean;
}

export const InputNestedControlCard = ({column, valueInstance, disabled}: Props) => {
  const {t} = useTranslation();
  const {instance, isComplete, canEdit, updateValues} = useControlCardInstanceContext();
  const hasError = useHasControlCardValueError(valueInstance);
  const {push} = useIonRouter();
  const [presentLoading, dismissLoading] = useIonLoading();
  const [isCreating, setIsCreating] = useState(false);
  const [appError, setAppError] = useState<ApplicationError | null>(null);

  const localDisabled = isComplete || !!disabled || !canEdit || isCreating;
  const value = valueInstance.value;

  const create = useCallback(async () => {
    if (
      value.__typename !== "ControlCardNewManualCreateInstanceOwningRefValue" &&
      value.__typename !== "ControlCardNewAutoCreateInstanceOwningRefValue"
    ) {
      return;
    }
    setIsCreating(true);
    setAppError(null);
    await presentLoading({message: t("control-card.nested.creating")});
    try {
      const result = await query(CreateNestedControlCardInstanceDocument, {
        input: {
          parentInstanceId: instance.id,
          valueInstanceId: valueInstance.id
        }
      });
      updateValues(result.createNestedControlCardInstance.values);
      push(linkEmplControlCardInstance(result.createNestedControlCardInstance.instance.id.instanceId));
    } catch (e) {
      setAppError(transformToApplicationError(e));
    } finally {
      await dismissLoading();
      setIsCreating(false);
    }
  }, [value.__typename, presentLoading, t, instance.id, valueInstance.id, updateValues, push, dismissLoading]);

  const open = useCallback(() => {
    if (value.__typename !== "ControlCardExistingInstanceOwningRefValue") return;
    push(linkEmplControlCardInstance(value.instanceId.instanceId));
  }, [value, push]);

  return (
    <InputIonCard $hasError={hasError} data-scroll-to-value-instance-id={valueInstance.id}>
      <IonCardHeader>
        <IonCardTitle>{column.title}</IonCardTitle>
        {isNotBlank(column.description) && <IonCardSubtitle>{column.description}</IonCardSubtitle>}
      </IonCardHeader>
      <IonCardContent>
        <DisplayAppError appError={appError} />
        <FlexColumnCenter>
          {value.__typename === "ControlCardExistingInstanceOwningRefValue" ? (
            (() => {
              const statusColor = value.instance.completedAt
                ? "success"
                : value.instance.notApplicableAt
                  ? "not-applicable"
                  : "incomplete";
              const statusText = value.instance.completedAt
                ? t("control-card.nested.status.completed")
                : value.instance.notApplicableAt
                  ? t("control-card.nested.status.not-applicable")
                  : t("control-card.nested.status.pending");
              return (
                <>
                  <IonButton color={statusColor} onClick={open} size="large">
                    {t("control-card.nested.open")}
                    <IonIcon icon={arrowForwardOutline} slot="end" />
                  </IonButton>
                  <IonBadge color={statusColor}>{statusText}</IonBadge>
                </>
              );
            })()
          ) : (
            <IonButton color="primary" disabled={localDisabled} onClick={create} size="large">
              <IonIcon icon={addOutline} slot="start" />
              {t("control-card.nested.create")}
            </IonButton>
          )}
          <DisplayValueInstanceError column={column} disabled={localDisabled} valueInstance={valueInstance} />
        </FlexColumnCenter>
      </IonCardContent>
    </InputIonCard>
  );
};
